import React, { useState } from 'react';
import DtvText from '@directv-digital/dds.ui.dtv-text';
import DtvBox from '@directv-digital/dds.ui.dtv-box';
import CardMedia from '@mui/material/CardMedia';
import RadioButton from '@directv-digital/fusion.atoms.radio-button';
import  Button  from '@directv-digital/fusion.atoms.button';
import BackButton from "./back-button";
import { ApprovalIconNoOutline } from '../utils';
import DtvTheme, { colorPalette, MOBILE_WIDTH, TABLET_WIDTH, DESKTOP_WIDTH } from '@directv-digital/dds.ui.dtv-theme';
import { fireCustomLinkClickGA } from "@directv-digital/dds.services.analytics-service";
import { setCookie } from '@directv-digital/dds.services.cookie-service';

const bps = DtvTheme.breakpoints;

interface IDevicesRadioOption {
  value: number | undefined;
  index: number | undefined;
  devices?: number;
  addLater?: boolean;
}

type Breakpoint = "mobile" | "tablet" | "desktop" | "desktop-large";

const Recommendations = ({
  id,
  recommendationsBgColor,
  bulletsHeroImg,
  bulletsHeading,
  bulletsSubheading,
  bulletsList,
  optionsHeading,
  optionsSubheading,
  saveCtaLabelDefault,
  saveCtaLabelConfirmed,
  saveCtaConfirmationMsg,
  radioOptions,
  hasCartItems,
  handleToggleRedirectModal,
  onRestartClickFn
}) => {
  const [radioSelection, setRadioSelection] = useState<IDevicesRadioOption>({
    value: undefined,
    index: undefined
  });
  const [radioErrorMsg, setRadioErrorMsg] = useState("");
  const [confirmationMsg, setConfirmationMsg] = useState("");
  const [backButtonLabel, setBackButtonLabel] = useState("Back");
  const [breakpoint, setBreakpoint] = useState<Breakpoint>("tablet")

  React.useEffect(() => {
    let resizeDebounceTimer;
    function handleResize() {
      clearTimeout(resizeDebounceTimer);
      resizeDebounceTimer = setTimeout(() => {
        if (window.innerWidth > DESKTOP_WIDTH) {
          setBreakpoint("desktop-large")
        } else if (window.innerWidth > TABLET_WIDTH) {
          setBreakpoint("desktop");
        } else if (window.innerWidth > MOBILE_WIDTH) {
          setBreakpoint("tablet");
        } else {
          setBreakpoint("mobile");
        }
      }, 200)
    }
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleRadioChange = e => {
    const option = radioOptions.find(optionInner => optionInner.value === Number(e.target.value));
    setRadioSelection(option);
    setRadioErrorMsg("");
    setConfirmationMsg("");
    setBackButtonLabel('Back');
  }

  const handleRestartClick = () => {
    setBackButtonLabel('Back');
    onRestartClickFn(backButtonLabel, backButtonLabel === "Restart");
    setRadioSelection({
      value: undefined,
      index: undefined
    });
    setConfirmationMsg("");
  }
  const handleOnClick = (label: any ) => {
    const dataForAnalytics = {
      event: 'trackEvent',
      event_name: 'custom_link_click',
      linkName: `${label?.toLowerCase()}`,
      linkPosition: 'panel_devicewizard',
      linkDestination: `${window.location.origin}${window.location.pathname}`
    }
    fireCustomLinkClickGA(dataForAnalytics);
  };
  const handleSaveSelection = (label: any) => {
    handleOnClick(`question3_${label}`);
    if (sessionStorage === undefined) {
      setRadioErrorMsg("Having trouble saving your selection. Please try again later.");
      return;
    }
    if (radioSelection.value === undefined) {
      setRadioErrorMsg("Please make a selection before moving forward.");
      return;
    }

    // remove possible previous selection
    // eslint-disable-next-line no-console
    console.debug("EquipmentWizard ## Removing previous quiz result from session storage");
    sessionStorage?.removeItem("device-intent");
    setBackButtonLabel('Restart');
    // If I'll add my devices later is selected
    if (Boolean(radioSelection?.addLater) === true) {
      setConfirmationMsg(saveCtaConfirmationMsg);
      return;
    }
    // set 30 day session cookie, and set session storage "device-intent" value
    setCookie("de_cnt", radioSelection?.devices?.toString(), "/", 14, true, '.directv.com');
    sessionStorage.setItem("device-intent", radioSelection?.devices?.toString());
    // eslint-disable-next-line no-console
    console.debug("EquipmentWizard ## Added quiz result to session cookie/storage: ", radioSelection?.devices?.toString());
    setConfirmationMsg(saveCtaConfirmationMsg);
    if (!hasCartItems) {
      setTimeout(() => {
        handleToggleRedirectModal('');
      }, 1500)
    }
  };

  return (
    <DtvBox id={`${id}-recs-ctnr`}
      sx={{
        display: "flex",
flexDirection: "column",
        margin: "20px 20px 36px 20px",
        ...breakpoint === "tablet" && {
          margin: "42px 56px 36px 56px"
        },
        ...(breakpoint !== "mobile" && breakpoint !== "tablet") && {
          gap: "30px",
          margin: "26px 36px 36px 64px",
          flexDirection: "row",
          alignItems: "stretch"
        },
        ...breakpoint === "desktop-large" && {
          flexDirection: "row !important",
          alignItems: "stretch"
        },
        borderRadius: "8px"
      }}
    >
        <DtvBox id={`${id}-recs-heading-flexwrap`}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            height: "100%",
            ...(breakpoint !== "mobile" && breakpoint !== "tablet") && {
              width: "70%"
            }
          }}>
            <DtvBox id={`${id}-recs-heading-ctnr`}
              sx={{
                width: "100%",
                ...breakpoint === "mobile" && {
                  marginTop: "20px",
                  order: 2
                },
                ...breakpoint !== "mobile" && {
                  order: 1,
                  marginBottom: "24px"
                },
                ...(breakpoint !== "mobile" && breakpoint !== "tablet") && {
                  marginBottom: "28px",
                  alignSelf: "flex-end",
                  marginTop: "15px"
                }
              }}>
                {bulletsHeading?.content && (
                  <DtvText
                    content={bulletsHeading?.content}
                    variant="subhead1medium"
                    component="h3"
                    sx={{
                      width: "100%",
                      fontSize: "24px",
                      letterSpacing: "-0.48px",
                      lineHeight: "28.8px",
                      ...(breakpoint !== "mobile" && breakpoint !== "tablet") && {
                        fontSize: "36px"
                      }
                    }}

                  />
                )}
                {bulletsSubheading?.content && (
                  <DtvText
                    content={bulletsSubheading?.content}
                    variant="body1"
                    sx={{
                      width: "100%",
                      fontSize: "16px",
                      lineHeight: "20.8px",
                      paddingTop: "10px",
                      ...breakpoint === "tablet" && {
                        paddingTop: "12px"
                      },
                      ...(breakpoint !== "mobile" && breakpoint !== "tablet") && {
                        paddingTop: "16px",
                        fontSize: "20px"
                      }
                    }}
                  />
                )}
            </DtvBox>
            <CardMedia component="img"
              src={bulletsHeroImg?.src}
              alt={bulletsHeroImg?.alt || "Gemini Device Image"}
              sx={{
                width: "100%",
                order: 1,
                ...breakpoint !== "mobile" && {
                  width: "calc(60% - 20px)",
                  marginRight: "20px",
                  height: "100%",
                  order: 2
                },
                height: "auto",
                borderRadius: "8px"
              }}
            />
            {bulletsList?.length && (
              <DtvBox sx={{
                display: "flex",
flexDirection: "column",
gap: "16px",
                order: 3,
                width: "100%",
                ...breakpoint === "mobile" && {
                  padding: "16px 0 20px 0"
                },
                ...breakpoint !== "mobile" && {
                  width: "40%"
                }
              }}>
                {bulletsList?.map((bullet, index) => (
                  <DtvBox key={index} sx={{
                    display: "flex",
                    width: "100%",
                    alignItems: "flex-start",
                    gap: "8px"
                  }}>
                    {bullet.iconUrl &&
                      <img src={bullet?.iconUrl} width={24} height={24} alt="text indicator" />
                    }
                    {bullet.text && (
                      <DtvText
                        content={bullet.text}
                        variant={"body2"}
                        sx={{
                          fontSize: "16px",
                          lineHeight: "20.8px",
                          color: colorPalette.blue[900]
                        }}
                        tooltip={bullet.tooltip?.content ? bullet.tooltip : null}
                      />
                    )}
                  </DtvBox>
                ))}
              </DtvBox>
            )}
        </DtvBox>

        <DtvBox id={`${id}-recs-flex`}
          sx={{
            backgroundColor: recommendationsBgColor,
borderRadius: "8px",
            display: "flex",
flexDirection: "column",
            ...breakpoint === "mobile" && {
              padding: "20px 20px 16px 20px"
            },
            ...breakpoint === "tablet" && {
              padding: "24px 28px 28px 28px",
              marginTop: "20px"
            },
            ...breakpoint === "desktop" && {
              padding: "16px 28px 28px 28px",
              width: "40%"
            },
            ...breakpoint === "desktop-large" && {
              width: "30%",
              padding: "16px 28px 28px 28px"
            },
            "& .MuiFormGroup-root": {
              // radio flex container
              "& label": {
                alignItems: "center",
                margin: "8px 0"
              },
              // radio button
              "& .MuiFormControlLabel-root .MuiRadio-root": {
                height: "24px !important",
width: "24px !important"
              },
              // first radio button
              "& label:first-of-type": {
                marginTop: "0 !important"
              },
              // last radio button
              "& label:last-of-type": {
                marginBottom: "0 !important"
              },
              // radio button label
              "& .MuiFormControlLabel-root .MuiTypography-root": {
                fontSize: "16px !important"
              }
            }
          }}>
          <DtvBox id={`${id}-recs-flex-1`} sx={{
            ...breakpoint === "desktop-large" && {
              height: "100%"
            }
          }}>
            {optionsHeading?.content && (
              <DtvText
                content={optionsHeading?.content}
                variant={"subhead2medium"}
                component="p"
                sx={{
                  width: "100%",
                  letterSpacing: "-0.48px",
lineHeight: "28.8px",
                  marginBottom: "16px",
                  fontSize: "24px",
                  ...(breakpoint !== "mobile" && breakpoint !== "tablet") && {
                    marginBottom: "20px"
                  }
                }}
              />
            )}
            {optionsSubheading?.content && (
              <DtvText
                content={optionsSubheading?.content}
                variant={"body2"}
                sx={{
                  width: "100%",
                  lineHeight: "20.8px",
letterSpacing: "-0.48px",
                  fontSize: "16px",
                  marginBottom: "20px",
                  ...breakpoint === "tablet" && {
                    marginBottom: "26px"
                  }
                }}
              />
            )}
            <RadioButton
              mode="light"
              variant="Radio Label"
              radiobuttonoptions={radioOptions?.length ?
                radioOptions.map(op => ({
                  ...op,
                  checked: radioSelection?.value === op.value
                })) :
              []}
              onChange={handleRadioChange}
              analytics={{
                tag: "Radiobutton",
                event: 'Checked',
                event_name: 'Radiobutton'
              }}
            />
            {radioErrorMsg && (
              <DtvBox sx={{
                display: "inline-flex",
                gap: "12px",
                alignItems: "flex-start",
                padding: "14px 0 0 2px",
                ...breakpoint === "tablet" && {
                  padding: "14px 0 0 0"
                }
              }}>
                <img src={"https://www.directv.com/dtvassets/global/icons/fusion/alert-icon-error.svg"}
                  alt="helper text icon"
                  width="24" height="24"
                />
                <DtvText content={radioErrorMsg} variant="body2"
                  sx={{
                    color: colorPalette.error1.main,
                    fontSize: "14px",
                    lineHeight: "20px"
                  }}
                />
              </DtvBox>
            )}
          </DtvBox>
          <DtvBox id={`${id}-recs-flex-2`}>
            <DtvBox id={`${id}-recs-nav`} sx={{
              width: "100%",
overflow: "hidden",
              display: "flex",
              justifyContent: "space-between",
              flexWrap: 'wrap',
              paddingTop: "16px",
              paddingBottom: "16px",
              ...breakpoint === "mobile" && {
                gap: "6px",
                justifyContent: "space-evenly"
              },
              ...breakpoint === "tablet" && {
                justifyContent: "space-evenly"
              },
              ...breakpoint === "tablet" && {
                justifyContent: "space-evenly"
              },
              ...(breakpoint !== "mobile" && breakpoint !== "tablet") && {
                // justifyContent: "center",
                justifyContent: "space-evenly",
                paddingTop: "26px",
                paddingBottom: "26px"
              }
            }}>
              <BackButton label={backButtonLabel} onClickFn={handleRestartClick}
                sx={{
                  ...breakpoint === "mobile" && {
                    display: "none"
                  },
                  ...breakpoint === "tablet" && {
                    width: "50%",
                    maxWidth: "150px"
                  },
                  ...breakpoint === "tablet" && {
                    maxWidth: "200px",
                    paddingLeft: 0
                  }
                }}
              />
              <Button
                buttontype='primary' mode='light'
                size='medium'
                left-icon={<ApprovalIconNoOutline />}
                onClick={() => handleSaveSelection(!confirmationMsg ? saveCtaLabelDefault : saveCtaLabelConfirmed)}
                sx={{
                  margin: 0,
                  ...breakpoint === "mobile" && {
                    width: "100% !important",
                    padding: "0px 5px !important"
                  },
                  ...breakpoint === "tablet" && {
                    width: "50%",
                    maxWidth: "200px"
                  },
                  ...breakpoint === "tablet" && {
                    maxWidth: "300px"
                  },
                  [bps.down(bps.values.lg)]: {
                    width: "60%"
                  },
                  // width: "200px",
                  // maxWidth: "60%",
                  // ...breakpoint !== "mobile" && {
                  //   width: "100%",
                  //   maxWidth: "250px"
                  // },
                  // ...(breakpoint !== "mobile" && breakpoint !== "tablet") && {
                  //   width: "100%",
                  //   maxWidth: "250px"
                  // },
                  "& svg": {
                    width: confirmationMsg ? '20px' : 0,
                    height: confirmationMsg ? '20px' : 0,
                    transition: 'width 300ms ease-in 100ms, height 300ms ease-in 100ms'
                  }
                }}
                aria-label={!confirmationMsg ? saveCtaLabelDefault : saveCtaLabelConfirmed}
              >{!confirmationMsg ? saveCtaLabelDefault : saveCtaLabelConfirmed}
              </Button>
              <BackButton label={backButtonLabel} onClickFn={handleRestartClick}
                sx={{
                  ...breakpoint === "mobile" && {
                    padding: "0px 5px !important",
                    width: "calc(40% - 6px)",
                    minWidth: "90px",
                    marginTop: "5px !important",
                    display: "inline-flex"
                  },
                  ...breakpoint !== "mobile" && {
                    display: "none"
                  }
                }}
              />
            </DtvBox>
            {confirmationMsg && (
              <DtvText content={confirmationMsg} variant="body2"
                sx={{
                  display: "inline-flex",
                  color: "#176348 !important",
                  fontSize: "14px",
                  lineHeight: "20px",
                  paddingTop: "16px",
                  ...breakpoint !== "mobile" && {
                    justifyContent: "center"
                  }
                }}
              />
            )}
          </DtvBox>
        </DtvBox>
    </DtvBox>
  );
};

export default React.memo(Recommendations);
